import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { Fragment, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ColorScale from '_class/ColorScale';
import User from '_class/User';
import DateTime from '_class/DateTime';
import { useTranslate } from 'lib/translate';
import { capitalize } from 'helpers/content';
import { getUserSummary, getUserStatisticsDetailed } from 'actions/assessments';
import DisplayMatrix from 'components/Presentation/DisplayMatrix';
import DisplayGoal from 'components/Presentation/DisplayGoal';
import DisplayFeedbacks from 'components/Presentation/DisplayFeedbacks';
import { Spinner } from 'UI';
import { Bar } from 'UI/Graphs';
import { Block, Expandable, Icon, Flex } from '@haldor/ui';
import './_userAssessments.scss'; // TODO: (Next version?) calculate most values with useMemo to improve performance

var UserAssessments = function UserAssessments(props) {
  var dispatch = useDispatch();

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      open = _useState2[0],
      setOpen = _useState2[1];

  var _useState3 = useState(true),
      _useState4 = _slicedToArray(_useState3, 2),
      loading = _useState4[0],
      setLoading = _useState4[1];

  var currentUser = useSelector(function (state) {
    return state.user.currentUser;
  });
  var summary = useSelector(function (state) {
    var _state$assessments;

    return (_state$assessments = state.assessments) === null || _state$assessments === void 0 ? void 0 : _state$assessments.studentOverview;
  });
  var statistics = useSelector(function (state) {
    var _state$assessments2;

    return (_state$assessments2 = state.assessments) === null || _state$assessments2 === void 0 ? void 0 : _state$assessments2.studentStatistics;
  });
  var translate = useTranslate();
  var user = new User(currentUser);
  useEffect( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var params, start, end;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            params = {};

            if (props.courseId != null && props.courseId != 0) {
              params.courseId = props.courseId;
            }

            if (props.sectionId != null && props.sectionId != 0) {
              params.sectionId = props.sectionId;
            }

            if (props.showOnlyMyAssessments != null && props.showOnlyMyAssessments) {
              params.type = "MY";
            }

            setLoading(true);
            start = new DateTime(props.start).endOf('day').getUTCTimeStamp();
            end = new DateTime(props.end).endOf('day').getUTCTimeStamp();
            _context.next = 9;
            return Promise.all([dispatch(getUserSummary(props.studentId, start, end, params)), dispatch(getUserStatisticsDetailed(props.studentId, start, end, params))]);

          case 9:
            setLoading(false);
            return _context.abrupt("return", function () {
              return null;
            });

          case 11:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), [props.studentId, props.start, props.end, props.courseId, props.sectionId, props.showOnlyMyAssessments]);
  useEffect(function () {
    if (summary != null && open.length == 0) {
      setOpen(summary.courses.flatMap(function (course) {
        return ["".concat(course.id, "-assessment"), "".concat(course.id, "-feedback"), "".concat(course.id, "-courseSubject")];
      }));
    }
  }, [summary]);

  var getSummaryCourses = function getSummaryCourses() {
    if (summary == null) {
      return [];
    }

    if (props.courseId != null) {
      return summary.courses.filter(function (course) {
        return course.id == props.courseId;
      });
    }

    return summary.courses;
  };
  /**
   * @param {number} matrixId
   * @returns {array} parts
   */


  var getPartsByMatrixId = function getPartsByMatrixId(matrixId) {
    var _summary$assessmentBl;

    if (summary == null || !props.showMatrices) {
      return [];
    }

    return (_summary$assessmentBl = summary.assessmentBlockParts) === null || _summary$assessmentBl === void 0 ? void 0 : _summary$assessmentBl.filter(function (part) {
      return part.referenceType == 'matrix' && part.referenceId == matrixId;
    });
  };
  /**
   * @param {number} courseId
   * @returns {array} parts
   */


  var getGoalPartsByCourseId = function getGoalPartsByCourseId(courseId) {
    if (summary == null || !props.showGoals) {
      return [];
    }

    var blockParts = summary.assessmentBlockParts;
    blockParts = blockParts.filter(function (part) {
      if (part.referenceType == 'matrix') {
        return false;
      }

      return part.assessmentBlockPartRelationships.find(function (relationship) {
        return relationship.courseId == courseId;
      }) != null;
    });
    return blockParts;
  };
  /**
   * @param {array} parts
   * @returns {array} feedbacks
   */


  var getFeedbacksForParts = function getFeedbacksForParts(parts) {
    if (summary == null) {
      return [];
    }

    return JSON.parse(JSON.stringify(summary.assessmentFeedbacks)).filter(function (feedback) {
      return parts.find(function (part) {
        return feedback.assessmentBlockPartId == part.id;
      }) != null;
    });
  };
  /**
   * @returns {object} statistic
   */


  var getStudentStatistic = function getStudentStatistic() {
    if (statistics == null || props.studentId == null) {
      return null;
    }

    return statistics.find(function (statistic) {
      return statistic.studentId == props.studentId;
    });
  };
  /**
   * @param {object} part
   * @returns {object} statistic
   */


  var getPartStatistic = function getPartStatistic(part) {
    var _getStudentStatistic, _getStudentStatistic$;

    return (_getStudentStatistic = getStudentStatistic()) === null || _getStudentStatistic === void 0 ? void 0 : (_getStudentStatistic$ = _getStudentStatistic.studentStatistics) === null || _getStudentStatistic$ === void 0 ? void 0 : _getStudentStatistic$.find(function (statistic) {
      return statistic.type == part.referenceType.toUpperCase() && statistic.referenceId == part.referenceId;
    });
  };
  /**
   * @param {number} matrixId 
   * @returns {object} statistic
   */


  var getMatrixStatistic = function getMatrixStatistic(matrixId) {
    var _getStudentStatistic2, _getStudentStatistic3;

    return (_getStudentStatistic2 = getStudentStatistic()) === null || _getStudentStatistic2 === void 0 ? void 0 : (_getStudentStatistic3 = _getStudentStatistic2.studentStatistics) === null || _getStudentStatistic3 === void 0 ? void 0 : _getStudentStatistic3.find(function (statistic) {
      return statistic.type == 'MATRIX' && statistic.referenceId == matrixId;
    });
  };

  var getPartTitle = function getPartTitle(part, statistic, reference) {
    var index = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
    var colors = [];
    var extraTitle = null;
    var displayGraph = true;

    if (statistic != null) {
      var addRed = false;

      if (statistic.type == "MATRIX") {
        addRed = !reference.disableNotAchieved;
        displayGraph = statistic.statistics.find(function (stat) {
          return stat.count > 0;
        }) != null;
      }

      if (statistic.type == "ASSESSMENTGOAL") {
        addRed = reference.rows.find(function (row) {
          return row.cells.find(function (cell) {
            return cell.type == 'NotAchievedTextField';
          });
        }) != null;
        statistic.statistics.forEach(function (stat) {
          if (stat.assessmentDetails.length > 0) {
            extraTitle = stat.assessmentDetails[0].navigationObject.title;
          }
        });
      }

      colors = new ColorScale(statistic.statistics.length).get(addRed);
    }

    return /*#__PURE__*/React.createElement("div", {
      className: "df aic bar-outer-container"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bar-title-container",
      style: {
        marginRight: '.55rem'
      }
    }, translate(part.referenceType), part.referenceType == 'assessmentGoal' ? ' ' + index : null, extraTitle != null ? ' - ' + extraTitle : null), statistic != null && displayGraph ? part.referenceType == 'assessmentGoal' ? /*#__PURE__*/React.createElement("div", {
      className: "assessment-goals-dots"
    }, statistic.statistics.map(function (items, index) {
      return /*#__PURE__*/React.createElement("div", {
        className: "goal-dot",
        style: {
          backgroundColor: "".concat(items.count > 0 ? colors[items.index] : null)
        },
        key: index
      });
    })) : /*#__PURE__*/React.createElement(Bar, {
      data: statistic.statistics.map(function (stat) {
        return {
          amount: stat.count,
          label: stat.count,
          color: colors[stat.index]
        };
      }),
      showAmount: true,
      showAllSteps: true
    }) : null);
  };
  /**
   * @param {string} target
   * @param {array} permissions
   * @param {string} referenceType
   */


  var renderPermission = function renderPermission(target, permissions, referenceType) {
    var active = permissions.find(function (perm) {
      return perm.target == target && perm.referenceType == referenceType;
    }) != null;
    return /*#__PURE__*/React.createElement("div", {
      className: "permission"
    }, /*#__PURE__*/React.createElement(Icon, {
      name: active ? "Eye" : "Eye_Off"
    }), translate(capitalize(target)));
  };
  /**
   * TODO: Replace this with the DisplayFeedbacks component since it pretty much contains the same code
   * @param {object} feedback
   * @param {number} index
   */


  var renderFeedback = function renderFeedback(feedback, index) {
    var _summary$assessmentBl2;

    var resource = summary === null || summary === void 0 ? void 0 : (_summary$assessmentBl2 = summary.assessmentBlockParts) === null || _summary$assessmentBl2 === void 0 ? void 0 : _summary$assessmentBl2.find(function (part) {
      return part.id == feedback.assessmentBlockPartId;
    });
    var permissions = [];

    if (resource != null && !feedback.isPrivate) {
      permissions = resource.permissions;
    }

    return /*#__PURE__*/React.createElement("div", {
      className: "feedback",
      style: {
        paddingLeft: 0
      },
      key: feedback.id
    }, /*#__PURE__*/React.createElement("div", {
      className: "df size-12 feedback-item-header",
      style: {
        marginBottom: '.4rem'
      }
    }, /*#__PURE__*/React.createElement("div", null, feedback.navigationObject != null ? feedback.navigationObject.referenceType == 'PLAN' ? translate('Plan') : translate('Assignment') : null, feedback.navigationObject != null ? ' • ' + feedback.navigationObject.title : null), /*#__PURE__*/React.createElement("div", null, feedback.editorUser != null ? feedback.editorUser.firstName + ' ' + feedback.editorUser.lastName : null, ' • ' + new DateTime(feedback.published ? feedback.published : feedback.created).getLongDate())), /*#__PURE__*/React.createElement("div", {
      className: "df size-12 feedback-item"
    }, /*#__PURE__*/React.createElement("div", {
      dangerouslySetInnerHTML: {
        __html: feedback.text
      }
    }), !user.isStudent() ? /*#__PURE__*/React.createElement("div", {
      className: "permissions"
    }, renderPermission('STUDENT', permissions, 'ASSESSMENTFEEDBACK'), renderPermission('GUARDIAN', permissions, 'ASSESSMENTFEEDBACK')) : null));
  };

  var renderAssessmentsWithOnlyFeedback = function renderAssessmentsWithOnlyFeedback() {
    if (summary == null || summary.assessmentBlockParts == null) {
      return null;
    }

    var parts = summary.assessmentBlockParts.filter(function (blockPart) {
      return blockPart.assessmentBlockPartRelationships.length == 0;
    });

    if (parts == null || parts.length == 0) {
      return null;
    }

    var isOpen = open.indexOf("only-feedbacks") > -1;
    return /*#__PURE__*/React.createElement(Block, null, /*#__PURE__*/React.createElement(Flex, {
      center: true,
      space: true
    }, /*#__PURE__*/React.createElement("span", {
      className: "title",
      style: {
        '--color': '#777'
      }
    }, translate('Assessments with only feedback')), /*#__PURE__*/React.createElement("div", {
      className: isOpen ? "expand-all active" : "expand-all",
      onClick: function onClick() {
        return toggleSection('only', 'feedbacks');
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "ArrowLeft"
    }), isOpen ? translate('Close all') : translate('Show all'))), /*#__PURE__*/React.createElement(Expandable, {
      color: "#777",
      open: open.includes("only-feedbacks"),
      onChange: function onChange() {
        return toggleSection('only', 'feedbacks');
      },
      title: translate('Collected feedback')
    }, /*#__PURE__*/React.createElement("div", {
      className: "course-content collected-feedback"
    }, parts.map(function (part, index) {
      if (part == null || part.referenceType != 'assessmentonlyfeedback') {
        return null;
      }

      var feedbacks = getFeedbacksForParts([part]).filter(function (f) {
        return !f.isPrivate;
      });
      if (feedbacks.length == 0) return null;
      return /*#__PURE__*/React.createElement(Expandable, {
        ignorePropsUpdate: true,
        open: true,
        key: part.id,
        title: getPartTitle(part, null, null, index + 1)
      }, feedbacks.length > 0 ? /*#__PURE__*/React.createElement("div", {
        className: "display-feedbacks",
        style: {
          padding: 0,
          border: 0,
          margin: 0
        }
      }, feedbacks.map(renderFeedback)) : null);
    }))), user.isMentor() || user.isTeacher() ? /*#__PURE__*/React.createElement(Expandable, {
      title: translate('Collected personal notes')
    }, /*#__PURE__*/React.createElement("div", {
      className: "course-content"
    }, parts.map(function (part, index) {
      if (part == null || part.referenceType != 'assessmentonlyfeedback' || currentUser == null) {
        return null;
      }

      var feedbacks = getFeedbacksForParts([part]).filter(function (f) {
        return f.isPrivate && f.editorUser != null && f.editorUser.userId == currentUser.id;
      });
      if (feedbacks.length == 0) return null;
      return /*#__PURE__*/React.createElement(Expandable, {
        key: part.id,
        title: translate('Personal note')
      }, /*#__PURE__*/React.createElement("div", {
        className: "display-feedbacks",
        style: {
          padding: 0,
          border: 0,
          margin: 0
        }
      }, feedbacks.map(renderFeedback)));
    }))) : null);
  };

  var renderGoalsWithoutCourse = function renderGoalsWithoutCourse() {
    if (summary == null || summary.assessmentBlockParts == null) {
      return null;
    }

    var parts = summary.assessmentBlockParts.filter(function (goal) {
      return goal.assessmentBlockPartRelationships.length == 0;
    });

    if (parts == null || parts.length == 0) {
      return null;
    }

    var isOpen = open.indexOf("goals-assessment") > -1 || open.indexOf("goals-feedback") > -1;
    return /*#__PURE__*/React.createElement(Block, null, /*#__PURE__*/React.createElement(Flex, {
      center: true,
      space: true
    }, /*#__PURE__*/React.createElement("span", {
      className: "title",
      style: {
        '--color': '#777'
      }
    }, translate('Assessment goals without course relation')), /*#__PURE__*/React.createElement("div", {
      className: isOpen ? "expand-all active" : "expand-all",
      onClick: function onClick() {
        return toggleCourse('goals');
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "ArrowLeft"
    }), isOpen ? translate('Close all') : translate('Show all'))), /*#__PURE__*/React.createElement(Expandable, {
      color: "#777",
      open: open.includes("goals-assessment"),
      onChange: function onChange() {
        return toggleSection('goals', 'assessment');
      },
      title: translate('Assessment')
    }, /*#__PURE__*/React.createElement("div", {
      className: "course-content"
    }, parts.map(function (part, index) {
      if (part == null) {
        return null;
      }

      var goal = summary.assessmentGoals.find(function (goal) {
        return goal.id == part.referenceId;
      });
      var statistic = getPartStatistic(part);
      var feedbacks = getFeedbacksForParts([part]);

      if (goal == null || (goal === null || goal === void 0 ? void 0 : goal.rows.length) == 0 && feedbacks.length == 0) {
        return null;
      }

      return /*#__PURE__*/React.createElement(Expandable, {
        ignorePropsUpdate: true,
        open: true,
        key: goal.id,
        title: getPartTitle(part, statistic, goal, index + 1)
      }, goal != null ? /*#__PURE__*/React.createElement(DisplayGoal, {
        goal: goal,
        statistic: statistic
      }) : null, feedbacks.length > 0 ? /*#__PURE__*/React.createElement(DisplayFeedbacks, {
        feedbacks: feedbacks,
        resource: part
      }) : null);
    }))), /*#__PURE__*/React.createElement(Expandable, {
      color: "#777",
      open: open.includes("goals-feedback"),
      onChange: function onChange() {
        return toggleSection('goals', 'feedback');
      },
      title: translate('Collected feedback')
    }, /*#__PURE__*/React.createElement("div", {
      className: "course-content collected-feedback"
    }, parts.map(function (part, index) {
      var goal = summary.assessmentGoals.find(function (goal) {
        return goal.id == part.referenceId;
      });

      if (goal == null || part == null) {
        return null;
      }

      var feedbacks = getFeedbacksForParts([part]).filter(function (f) {
        return !f.isPrivate;
      });
      var statistic = getPartStatistic(part);
      if (feedbacks.length == 0) return null;
      return /*#__PURE__*/React.createElement(Expandable, {
        ignorePropsUpdate: true,
        open: true,
        key: goal.id,
        title: getPartTitle(part, statistic, goal, index + 1)
      }, feedbacks.length > 0 ? /*#__PURE__*/React.createElement("div", {
        className: "display-feedbacks",
        style: {
          padding: 0,
          border: 0,
          margin: 0
        }
      }, feedbacks.map(renderFeedback)) : null);
    }))), user.isMentor() || user.isTeacher() ? /*#__PURE__*/React.createElement(Expandable, {
      title: translate('Collected personal notes')
    }, /*#__PURE__*/React.createElement("div", {
      className: "course-content"
    }, parts.map(function (part, index) {
      var goal = summary.assessmentGoals.find(function (goal) {
        return goal.id == part.referenceId;
      });

      if (goal == null || part == null) {
        return null;
      }

      var feedbacks = getFeedbacksForParts([part]).filter(function (f) {
        return f.isPrivate;
      });
      var statistic = getPartStatistic(part);
      if (feedbacks.length == 0) return null;
      return /*#__PURE__*/React.createElement(Expandable, {
        key: goal.id,
        title: translate('Personal note')
      }, /*#__PURE__*/React.createElement("div", {
        className: "display-feedbacks",
        style: {
          padding: 0,
          border: 0,
          margin: 0
        }
      }, feedbacks.map(renderFeedback)));
    }))) : null);
  };
  /**
   * @param {number} id
   * @param {string} section
   */


  var toggleSection = function toggleSection(id, section) {
    var newOpen = _toConsumableArray(open);

    var identifier = "".concat(id, "-").concat(section);
    var index = open.indexOf(identifier);

    if (index > -1) {
      if (section === 'courseSubject') {
        newOpen.filter(function (x) {
          return x !== "".concat(id, "-assessment") && x !== "".concat(id, "-feedback");
        });
      }

      newOpen.splice(index, 1);
    } else {
      newOpen.push(identifier);

      if (section === 'courseSubject') {
        newOpen.push("".concat(id, "-assessment"));
        newOpen.push("".concat(id, "-feedback"));
      }
    }

    setOpen(newOpen);
  };

  var toggleAll = function toggleAll(e) {
    e.preventDefault();

    if (open.length > 0) {
      setOpen([]);
    } else {
      if (summary != null) {
        setOpen(summary.courses.flatMap(function (course) {
          return ["".concat(course.id, "-assessment"), "".concat(course.id, "-feedback"), "".concat(course.id, "-courseSubject"), "goals-assessment", "goals-feedback", "only-feedbacks"];
        }));
      }
    }
  };
  /**
   * @param {number} courseId
   */


  var toggleCourse = function toggleCourse(courseId) {
    var assessment = open.indexOf("".concat(courseId, "-assessment"));
    var feedback = open.indexOf("".concat(courseId, "-feedback"));

    if (assessment > -1 || feedback > -1) {
      var newOpen = _toConsumableArray(open);

      if (assessment > -1) newOpen.splice(assessment, 1);
      feedback = newOpen.indexOf("".concat(courseId, "-feedback"));
      if (feedback > -1) newOpen.splice(feedback, 1);
      setOpen(newOpen);
    } else {
      setOpen(["".concat(courseId, "-assessment"), "".concat(courseId, "-feedback")].concat(_toConsumableArray(open)));
    }
  };

  if (loading) {
    return /*#__PURE__*/React.createElement("div", {
      className: "user-asessments assessment-block"
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: '1rem'
      }
    }, /*#__PURE__*/React.createElement("h3", null, translate('knowledge-overview'))), /*#__PURE__*/React.createElement(Spinner, {
      center: true
    }));
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "user-assessments assessment-block"
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '1rem'
    }
  }, /*#__PURE__*/React.createElement(Flex, {
    center: true,
    space: true
  }, /*#__PURE__*/React.createElement("h3", null, translate('knowledge-overview')), summary != null ? /*#__PURE__*/React.createElement("div", {
    className: open.length > 0 ? "expand-all active" : "expand-all",
    onClick: toggleAll
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "ArrowLeft"
  }), open.length > 0 ? translate('Close all') : translate('Show all')) : null)), summary != null ? getSummaryCourses().map(function (course) {
    // TODO: Condense and optimize this render method
    // Do we have unnecessary data or do we fetch the same data twice?
    var schoolType = translate(course.typeOfSchooling);
    var courseTitle = "".concat(course.title, " - ").concat(course.year != null ? course.year : '', " ").concat(course.courseCode, " (").concat(schoolType, ")");
    var courseGoalParts = getGoalPartsByCourseId(course.id);
    var isOpen = open.indexOf("".concat(course.id, "-courseSubject")) > -1;
    ;
    var titleHeader = /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        flexDirection: 'row'
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        display: 'flex',
        flexGrow: 1
      }
    }, courseTitle), /*#__PURE__*/React.createElement("div", {
      className: isOpen ? "expand-all active" : "expand-all",
      onClick: function onClick() {
        return toggleCourse(course.id);
      },
      style: {
        marginRight: '1.6rem'
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "ArrowLeft"
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: '1rem',
        fontWeight: 'normal'
      }
    }, isOpen ? translate('Close all') : translate('Show all'))));
    return /*#__PURE__*/React.createElement("div", {
      className: 'assessmentBlockContainer'
    }, /*#__PURE__*/React.createElement(Block, {
      key: course.id
    }, /*#__PURE__*/React.createElement(Expandable, {
      open: open.includes("".concat(course.id, "-courseSubject")),
      onChange: function onChange() {
        return toggleSection(course.id, 'courseSubject');
      },
      color: course.colorCode,
      title: titleHeader
    }, /*#__PURE__*/React.createElement(Expandable, {
      open: open.includes("".concat(course.id, "-assessment")),
      onChange: function onChange() {
        return toggleSection(course.id, 'assessment');
      },
      color: course.colorCode,
      title: translate('Assessment')
    }, /*#__PURE__*/React.createElement("div", {
      className: "course-content"
    }, courseGoalParts.map(function (part, index) {
      if (part == null) {
        return null;
      }

      var goal = summary.assessmentGoals.find(function (goal) {
        return goal.id == part.referenceId;
      });
      var statistic = getPartStatistic(part);
      var feedbacks = getFeedbacksForParts([part]);

      if (goal == null || (goal === null || goal === void 0 ? void 0 : goal.rows.length) == 0 && feedbacks.length == 0) {
        return null;
      }

      return /*#__PURE__*/React.createElement(Expandable, {
        ignorePropsUpdate: true,
        open: true,
        key: goal.id,
        title: getPartTitle(part, statistic, goal, index + 1)
      }, goal != null ? /*#__PURE__*/React.createElement(DisplayGoal, {
        goal: goal,
        statistic: statistic
      }) : null, feedbacks.length > 0 ? /*#__PURE__*/React.createElement(DisplayFeedbacks, {
        feedbacks: feedbacks,
        resource: part,
        isStudentOverView: props.studentOverView
      }) : null);
    }), course.details.map(function (detail) {
      var parts = getPartsByMatrixId(detail.matrixID);

      if (parts.length == 0) {
        return null;
      }

      var statistic = getMatrixStatistic(detail.matrixID);
      var feedbacks = getFeedbacksForParts(parts);
      return /*#__PURE__*/React.createElement(Expandable, {
        ignorePropsUpdate: true,
        key: detail.matrixID,
        title: getPartTitle(parts[0], statistic, detail.matrix)
      }, /*#__PURE__*/React.createElement(DisplayMatrix, {
        matrix: detail.matrix,
        statistic: statistic
      }), feedbacks.length > 0 ? /*#__PURE__*/React.createElement(DisplayFeedbacks, {
        feedbacks: feedbacks,
        resources: parts
      }) : null);
    }))), /*#__PURE__*/React.createElement(Expandable, {
      open: open.includes("".concat(course.id, "-feedback")),
      onChange: function onChange() {
        return toggleSection(course.id, 'feedback');
      },
      color: course.colorCode,
      title: translate('Collected feedback')
    }, /*#__PURE__*/React.createElement("div", {
      className: "course-content collected-feedback"
    }, courseGoalParts.map(function (part, index) {
      var goal = summary.assessmentGoals.find(function (goal) {
        return goal.id == part.referenceId;
      });

      if (goal == null || part == null) {
        return null;
      }

      var feedbacks = getFeedbacksForParts([part]).filter(function (f) {
        return !f.isPrivate;
      });
      var statistic = getPartStatistic(part);
      if (feedbacks.length == 0) return null;
      return /*#__PURE__*/React.createElement(Expandable, {
        ignorePropsUpdate: true,
        open: true,
        key: goal.id,
        title: getPartTitle(part, statistic, goal, index + 1)
      }, /*#__PURE__*/React.createElement("div", {
        className: "display-feedbacks",
        style: {
          padding: 0,
          border: 0,
          margin: 0
        }
      }, feedbacks.map(renderFeedback)));
    }), course.details.map(function (detail) {
      var parts = getPartsByMatrixId(detail.matrixID);

      if (parts.length == 0) {
        return null;
      }

      var feedbacks = getFeedbacksForParts(parts).filter(function (f) {
        return !f.isPrivate;
      });
      if (feedbacks.length == 0) return null;
      return /*#__PURE__*/React.createElement(Expandable, {
        ignorePropsUpdate: true,
        open: true,
        key: detail.matrixID,
        title: getPartTitle(parts[0], null, detail.matrix)
      }, /*#__PURE__*/React.createElement("div", {
        className: "display-feedbacks",
        style: {
          padding: 0,
          border: 0,
          margin: 0
        }
      }, feedbacks.map(renderFeedback)));
    }))), user.isMentor() || user.isTeacher() ? /*#__PURE__*/React.createElement(Expandable, {
      color: course.colorCode,
      title: translate('Collected personal notes')
    }, /*#__PURE__*/React.createElement("div", {
      className: "course-content collected-feedback"
    }, courseGoalParts.map(function (part, index) {
      var goal = summary.assessmentGoals.find(function (goal) {
        return goal.id == part.referenceId;
      });

      if (goal == null || part == null) {
        return null;
      }

      var feedbacks = getFeedbacksForParts([part]).filter(function (f) {
        return f.isPrivate;
      });
      var statistic = getPartStatistic(part);
      if (feedbacks.length == 0) return null;
      return /*#__PURE__*/React.createElement(Expandable, {
        key: goal.id,
        title: translate('Personal note')
      }, /*#__PURE__*/React.createElement("div", {
        className: "display-feedbacks",
        style: {
          padding: 0,
          border: 0,
          margin: 0
        }
      }, feedbacks.map(renderFeedback)));
    }), course.details.map(function (detail) {
      var parts = getPartsByMatrixId(detail.matrixID);

      if (parts.length == 0) {
        return null;
      }

      var feedbacks = getFeedbacksForParts(parts).filter(function (f) {
        return f.isPrivate;
      });
      if (feedbacks.length == 0) return null;
      return /*#__PURE__*/React.createElement(Expandable, {
        key: detail.matrixID,
        title: getPartTitle(parts[0], null, detail.matrix)
      }, /*#__PURE__*/React.createElement("div", {
        className: "display-feedbacks",
        style: {
          padding: 0,
          border: 0,
          margin: 0
        }
      }, feedbacks.map(renderFeedback)));
    }))) : null)));
  }) : null, props.courseId == null ? renderGoalsWithoutCourse() : null, props.courseId == null ? renderAssessmentsWithOnlyFeedback() : null);
};

UserAssessments.defaultProps = {
  showGoals: true,
  showMatrices: true
};
export default UserAssessments;